import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./addTreatmentPlan.module.css";
import CloseButton from "../closeButton/closeButton.module";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "../../services/hooks";
import {
  userSelector,
  appointmentsProgramsDataSelector,
} from "../../services/selectors/user.selectors";
import Select, { ActionMeta, SingleValue, MultiValue } from "react-select";
import { initialAppointmentsProgram } from "../../utils/constants";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import useMediaQuery from "../../hooks/useMediaQuery";
import Modal from "../modals/modal/Modal";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import { getPrintDate } from "../../utils/utils";
import {
  useGetAllOpenTreatmentPlansByUserGuidMutation,
  useLazyGetAllMetaTablesQuery,
  usePostAppointmentProgramMutation,
  useUpdateTherapistTakingTreatmentPlanMutation,
  useGetProtocolsByCompanyQuery,
  useUpdateUnavailableTherapistTakingTreatmentPlanMutation,
} from "../../services/api/user.api";
import {
  IAppointmentsProgramsDataWithLabels,
  IAppointmentProgram,
  ITreatmentPlan,
} from "../../types/types";
import { useLocation, useNavigate } from "react-router-dom";
import { TTherapistUser } from "../../services/types/user.types";
import Loader from "../loader/loader";
import BackArrowButton from "../backArrowButton/backArrowButton";
import UserInfo from "../userInfo/userInfo";
import CompanyProgramsPopup from "../modals/companyProgramsPopup/companyProgramsPopup";
import useTreatmentPlanFormValidation from "../../hooks/useTreatmentPlanFormValidation";
import useTherapistOptions from "../../hooks/useTherapistOptions";

interface AddTreatmentPlanProps {
  program?: ITreatmentPlan | undefined;
  userDetails?: TTherapistUser & { UsersId?: number; Guid?: string };
  onSave?: (formData: Partial<IAppointmentProgram>) => void;
}

function AddTreatmentPlan({
  program,
  userDetails: userDetailsProp,
  onSave,
}: AddTreatmentPlanProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationUserDetails = location.state?.UserDetails; // for treatment plans opened from actions report user is passed as a prop
  const user = userDetailsProp ?? locationUserDetails;

  const [form, setForm] = useState(initialAppointmentsProgram);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showUnavailableTherapists, setShowUnavailableTherapists] = useState(false);
  const [showCompaniesProgramsModal, setShowCompaniesProgramsModal] = useState(false);

  const mobile = useMediaQuery("(max-width: 900px)");
  const therapist = useAppSelector(userSelector);
  const appointmentsProgramsData = useAppSelector(
    appointmentsProgramsDataSelector
  );

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  
   // Clears the selected therapist on form change
   const handleResetTherapistGuid = useCallback(() => {
    setForm((prev) => ({ ...prev, therapistsGuid: null }));
  }, []);
  
  const { 
    enabled: getTherapistsOptions, 
    setEnabled: setGetTherapistsOptions, 
    showCheckboxes, 
    isGettingTherapists,
    appointmentsProgramsTherapist,
    appointmentsProgramsUnavailableTherapists,
  } = useTherapistOptions({
    form,
    usersGuid: user?.Guid,
    adminCompanyId: therapist.companyId,
    adminGuid: therapist.guid,
    showUnavailable: showUnavailableTherapists,
    onResetTherapistGuid: handleResetTherapistGuid,
  });

  // Helper function to check if a therapist is available
  const checkAvailability = useCallback((therapistsId: number): boolean => {
    return appointmentsProgramsTherapist.some(
      (therapist) => therapist.therapistsId === therapistsId
    );
  }, [appointmentsProgramsTherapist]);
  
  const { isValid: isFormValid } = useTreatmentPlanFormValidation(form);
  const [updateUnavailableTherapistTakingTreatmentPlan] =
  useUpdateUnavailableTherapistTakingTreatmentPlanMutation();
  const [postAppointmentsPrograms, { isLoading: isSending }] =
    usePostAppointmentProgramMutation();
  const [updateTherapistTakingTreatmentPlan] =
    useUpdateTherapistTakingTreatmentPlanMutation();
  const [getAppointmentsPrograms] =
    useGetAllOpenTreatmentPlansByUserGuidMutation();
  const { data: protocolsData } = useGetProtocolsByCompanyQuery(
    { companyId: user?.CompanyId || therapist.companyId },
    { skip: !(user?.CompanyId || therapist.companyId) }
  );
  const [getAllMetaTables] = useLazyGetAllMetaTablesQuery();

  const treatmentRooms = appointmentsProgramsData.TreatmentRoomsList || [];
  const createDate = program?.appointmentsProgram?.createDate;
  const isValidForChanges = form.statusId === 1 || form.statusId === 4;
  const isClinicSelected = form.mediasId === 4;

  useAutosizeTextArea(textAreaRef.current, form.comments || "");
  
  // Initialize form with program data or user defaults
  useEffect(() => {    
    if (program?.appointmentsProgram) {
      setForm((prev) => ({
        ...prev,
        ...program?.appointmentsProgram,
        // Only use the protocol from the treatment plan itself, not from user
        protocolId: program.appointmentsProgram.protocolId ?? null,
      }));
    } else {
      const userDefaultProtocol = user?.QuestionnaireCompaniesHeadersId ?? user?.ProtocolId ?? null;
      if (userDefaultProtocol) {
        setForm((prev) => ({
          ...prev,
          protocolId: userDefaultProtocol,
        }));
      }
    }
  }, [program, user]);

  // Clear room selection when switching away from clinic mode
  useEffect(() => {
    if (!isClinicSelected) {
      setForm((prev) => ({ ...prev, treatmentRoomId: null }));
    }
  }, [isClinicSelected]);

  useEffect(() => {
    getAllMetaTables(therapist.companyId);
  }, [therapist.companyId, getAllMetaTables]);

  const onClose = () => {
    navigate(-1);
  };
  const sendForm = () => {
    const {
      usersId,
      therapistsId,
      createUsersGuid,
      createUsersId,
      updateUsersId,
      ...filteredAppointmentsProgram
    } = form;

    const payload = {
      ...filteredAppointmentsProgram,
      companyId: therapist.companyId,
      usersGuid: user?.Guid ?? "0",
      therapistsSpecializationsIds: 
        filteredAppointmentsProgram.therapistsSpecializationsIds?.length === 0
          ? null
          : filteredAppointmentsProgram.therapistsSpecializationsIds,
    };
        // If onSave callback is provided, just save locally and call it (for treatment plans opened from actions report)
        if (onSave) {
          const treatmentPlanData: Partial<IAppointmentProgram> = {
            ...payload,
            ...(form.id
              ? { updateUsersGuid: therapist.guid }
              : { createUsersGuid: therapist.guid }),
            //therapistsGuid: therapist.guid,
          };
          onSave(treatmentPlanData);
          return;
        }
    
        // Otherwise, submit immediately via API
    const apiCall =
      form.id && form.therapistsGuid
        ? form.available
          ? updateTherapistTakingTreatmentPlan({
            ...payload,
            updateUsersGuid: therapist.guid,
          })
          : updateUnavailableTherapistTakingTreatmentPlan({
            ...payload,
            updateUsersGuid: therapist.guid,
          })
        : postAppointmentsPrograms({
          ...payload,
          ...(form.id
            ? { updateUsersGuid: therapist.guid }
            : { createUsersGuid: therapist.guid }),
        });

    apiCall
      .then((res) => {
        if (res && "data" in res) {
          const responseData = res.data;
          if (responseData && typeof responseData === 'object') {
            if ('success' in responseData && (responseData as any).success === "false") {
              const errorMsg = (responseData as { message?: string }).message || null;
              setErrorMessage(errorMsg);
              setOpenModalError(true);
              return;
            }
          }

          // Update form state with the response data to ensure protocolId is preserved
          if (res.data && Array.isArray(res.data) && res.data.length > 0) {
            const updatedProgram = res.data[0] as unknown;
            if (
              updatedProgram &&
              typeof updatedProgram === 'object' &&
              'appointmentsProgram' in updatedProgram &&
              updatedProgram.appointmentsProgram &&
              typeof updatedProgram.appointmentsProgram === 'object' &&
              !Array.isArray(updatedProgram.appointmentsProgram)
            ) {
              const appointmentsProgram = updatedProgram.appointmentsProgram as Record<string, unknown>;
              setForm((prev) => ({
                ...prev,
                ...appointmentsProgram,
                // Ensure protocolId is preserved
                protocolId: (appointmentsProgram.protocolId as number | null | undefined) ?? prev.protocolId,
              }));
            }
          }

          setOpenModalSuccess(true);
          user?.Guid &&
            getAppointmentsPrograms({
              companyId: therapist.companyId,
              usersGuid: user.Guid,
            });
        }
      })
      .catch((err) => {
        // Check if error has a message in the data
        const errorMsg = err?.data?.message || err?.message || null;
        setErrorMessage(errorMsg);
        setOpenModalError(true);
        // console.log(err);
      });
  };

  const onSelectChange = useCallback(
    <
      T extends keyof IAppointmentsProgramsDataWithLabels,
      U extends IAppointmentsProgramsDataWithLabels[T][number]
    >(
      value: SingleValue<U>,
      { name }: ActionMeta<U>
    ) => {
      if (name && value && "value" in value) {
        setForm((prev) => ({
          ...prev,
          [name]: value.value,
        }));
      }
    },
    []
  );
  
  const onTherapistsSpecializationsChange = useCallback(
    (
      value: MultiValue<any> | null
    ) => {
      const ids = value ? value.map((item) => Number(item.value)) : [];

      setForm((prev) => ({
        ...prev,
        therapistsSpecializationsIds: ids,
      }));
    },
    []
  );
  
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: +value }));
    },
    []
  );

  const onCompanyProgramClick = (program: Partial<IAppointmentProgram>) => {
    setForm((prev) => ({ ...prev, ...program }));
    setShowCompaniesProgramsModal(false);
  };

  return (
    <section className={styles.section}>      
    {/* {!userDetailsProp && <CloseButton onClick={onClose} />} */}
    {!userDetailsProp && <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"center"}
        />}
        <h3 className={`title`}>{t("ADD_TREATMENT_PLAN_TITLE")}</h3>
        {user && (
          <div className={styles.userInfoContainer}>
            <UserInfo user={user} />
          </div>
        )}
         <button
                type={"button"}
                className={`${styles.addBtn}`}
                onClick={() => {
                  setShowCompaniesProgramsModal(true);
                }}
              >
                {t("SELECT_EXISTING_PROGRAM")}
                <span className={`plus`}>+</span>
              </button>
      <form className={styles.form}>
          <div className={styles.form__header}>
            {isValidForChanges ? (
              <p className={styles.form__note}>{t("REQUIRED_FIELDS_NOTE")}</p>
            ) : (
              <p className={styles.form__note}>
                {t("STATUS_CHANGE_NOT_ALLOWED")}
              </p>
            )}
          </div>
          <ul
            className={`${styles.form__list} ${!mobile && styles.form__list_four_columns}`}
          >
            <li>
              <label htmlFor="name" className={styles.form__label}>
                {t('NAME_1')}</label>
              <input
                type="text"
                className={`${styles.form__input} ${!isValidForChanges && styles.form__input_disabled
                  }`}
                name="name"
                id="name"
                disabled={!isValidForChanges}
                value={form.name || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </li>
            <li>
              <label
                htmlFor="countOfAppointments"
                className={styles.form__label}
              >
                {t("NUMBER_OF_APPOINTMENTS")}
              </label>
              <input
                type="number"
                className={`${styles.form__input} ${!isValidForChanges && styles.form__input_disabled
                  }`}
                name="countOfAppointments"
                id="countOfAppointments"
                value={form.countOfAppointments || ""}
                onChange={handleChange}
                disabled={!isValidForChanges}
              />
            </li>
            <li>
              <label
                htmlFor="appointmentsDurationsId"
                className={styles.form__label}
              >
                {t("APPOINTMENT_DURATION")}
              </label>
              <Select
                name={"appointmentsDurationsId"}
                options={appointmentsProgramsData.AppointmentsDurations}
                value={
                  appointmentsProgramsData.AppointmentsDurations.find(
                    (o) => o.value === form?.appointmentsDurationsId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label
                htmlFor="appointmentsSequenceId"
                className={styles.form__label}
              >
                {t("APPOINTMENT_FREQUENCY")}
              </label>
              <Select
                name={"appointmentsSequenceId"}
                options={appointmentsProgramsData.AppointmentsSequence}
                value={
                  appointmentsProgramsData.AppointmentsSequence.find(
                    (o) => o.value === form?.appointmentsSequenceId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={(value) => {
                  value &&
                    setForm((prev) => ({
                      ...prev,
                      appointmentsSequenceId: value.Id,
                    }));
                }}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="protocolId" className={styles.form__label}>
                {t("PROTOCOL_FOR_PATIENT")}
              </label>
              <Select
                name="protocolId"
                inputId="protocolId"
                options={protocolsData?.map(protocol => ({
                  value: protocol.id,
                  label: protocol.desc,
                })) || []}
                value={protocolsData?.find(protocol => protocol.id === form.protocolId) ? {
                  value: form.protocolId,
                  label: protocolsData.find(protocol => protocol.id === form.protocolId)?.desc || '',
                } : null}
                placeholder={t("SELECT_PROTOCOL")}
                className={styles.select}
                isClearable
                isDisabled={!isValidForChanges || (protocolsData && protocolsData.length === 0)}
                isLoading={!protocolsData}
                noOptionsMessage={() => t("NO_PROTOCOLS_AVAILABLE")}
                onChange={(value) => {
                  setForm((prev) => ({
                    ...prev,
                    protocolId: value?.value || null,
                  }));
                }}
              />
            </li>
            <li>
              <label htmlFor="roleId" className={styles.form__label}>
                {t("SUPPORT_ROLE")}
              </label>
              <Select
                name={"roleId"}
                options={appointmentsProgramsData.Roles}
                value={
                  appointmentsProgramsData.Roles.find(
                    (o) => o.value === form?.roleId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                isDisabled={!isValidForChanges}
                onChange={onSelectChange}
              />
            </li>
            <li>
              <label htmlFor="genderId" className={styles.form__label}>
                {t("GENDER")}
              </label>
              <Select
                name={"genderId"}
                options={appointmentsProgramsData.Genders}
                value={
                  appointmentsProgramsData.Genders.find(
                    (o) => o.value === form.genderId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="migzarId" className={styles.form__label}>
                {t("SECTOR")}
              </label>
              <Select
                name={"migzarId"}
                options={appointmentsProgramsData.Migzars}
                value={
                  appointmentsProgramsData.Migzars.find(
                    (o) => o.value === form.migzarId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="therapistsSpecializationsIds" className={styles.form__label}>
                {t("SPECIALIZATION")}
              </label>
              <Select
                name={"therapistsSpecializationsIds"}
                options={appointmentsProgramsData.TherapistsSpecializations}
                value={
                  form?.therapistsSpecializationsIds
                    ? appointmentsProgramsData.TherapistsSpecializations.filter(
                        (o) => form.therapistsSpecializationsIds?.includes(o.value as number)
                      )
                    : undefined
                }
                isMulti={true}
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={(newValue) => onTherapistsSpecializationsChange(newValue)}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="statusId" className={styles.form__label}>
                {t("COLUMN_STATUS")}
              </label>
              <Select
                name={"statusId"}
                options={appointmentsProgramsData.AppointmentsProgramsStatus}
                value={
                  appointmentsProgramsData.AppointmentsProgramsStatus.find(
                    (s) => s.Id === form.statusId
                  ) || undefined
                }
                placeholder={t("STATUS_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={true}
              />
            </li>
            <li>
              <label htmlFor="mediasId" className={styles.form__label}>
                {t("MEETING_MODE")}
              </label>
              <Select
                name={"mediasId"}
                options={appointmentsProgramsData.Medias}
                value={
                  appointmentsProgramsData.Medias.find(
                    (o) => o.value === form?.mediasId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            {/* Room Assignment Section - Show when clinic is selected */}
            {isClinicSelected && (
              <li>
                <label htmlFor="treatmentRoomId" className={styles.form__label}>
                  {t("SELECT_ROOM")}
                </label>
                <Select
                  name="treatmentRoomId"
                  options={[
                    { value: -1, label: t("SET_AUTOMATICALLY") },
                    ...treatmentRooms
                  ]}
                  value={
                    form.treatmentRoomId == null
                      ? { value: -1, label: t("SET_AUTOMATICALLY") }
                      : treatmentRooms.find(room => room.value === form.treatmentRoomId)
                  }
                  placeholder={t("SELECT_ROOM_PLACEHOLDER")}
                  className={styles.select}
                  onChange={(value) => {
                    const isAutomatic = value?.value === -1;
                    const roomId: number | null = isAutomatic ? null : (typeof value?.value === 'number' ? value.value : null);
                    setForm((prev) => ({
                      ...prev,
                      treatmentRoomId: roomId,
                    }));
                  }}
                  isDisabled={!isValidForChanges}
                />
              </li>
            )}
            <li>
              <label htmlFor="vetek" className={styles.form__label}>
                {t("TENURE")}
              </label>
              <input
                type="number"
                className={`${styles.form__input} ${!isValidForChanges && styles.form__input_disabled
                  }`}
                name="vetek"
                id="vetek"
                value={form.vetek || ""}
                onChange={handleChange}
                disabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="fundingSourcesId" className={styles.form__label}>
                {t("FUNDING_SOURCE")}
              </label>
              <Select
                name={"fundingSourcesId"}
                options={appointmentsProgramsData.FundingSources}
                value={
                  appointmentsProgramsData.FundingSources.find((o) => o.value === form.fundingSourcesId) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="treatmentTypesId" className={styles.form__label}>
                {t("TREATMENT_TYPE")}
              </label>

              <Select
                name={"treatmentTypesId"}
                options={appointmentsProgramsData.TreatmentTypes}
                value={
                  appointmentsProgramsData.TreatmentTypes.find((o) => o.value === form.treatmentTypesId) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="companyServicesId" className={styles.form__label}>
                {t("COMPANY_SERVICE")}
              </label>

              <Select
                name={"companyServicesId"}
                options={appointmentsProgramsData.CompanyServicesList}
                value={
                  appointmentsProgramsData.CompanyServicesList.find(
                    (o) => o.value === form.companyServicesId
                  ) || null
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                isClearable
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <div className={styles.form__checkboxes}>
                <input
                  type="checkbox"
                  disabled={!isValidForChanges || !isFormValid()}
                  name="therapistsId"
                  id="therapistsId"
                  checked={getTherapistsOptions}
                  onChange={() => {
                    setGetTherapistsOptions((prev) => !prev);
                  }}
                />

                <label htmlFor="therapistsId" className={styles.form__label}>
                  {t("ASSIGN_THERAPIST")}
                </label>
                {showCheckboxes && (
                  <>
                    <input
                      type="checkbox"
                      disabled={!isValidForChanges || !isFormValid()}
                      name="nonAvailableTherapistsId"
                      id="nonAvailableTherapistsId"
                      checked={showUnavailableTherapists}
                      onChange={() => {
                        setShowUnavailableTherapists((prev) => !prev);
                      }}
                    />
                    <label
                      htmlFor="nonAvailableTherapistsId"
                      className={styles.form__label}
                    >
                      {t('SHOW_NOT_AVAILABLE')}</label>
                  </>
                )}
              </div>
              {getTherapistsOptions ? (
                isGettingTherapists ? (
                  <Loader />
                ) : (
                  <Select
                    name={"therapistsId"}
                    options={
                      showUnavailableTherapists
                        ? appointmentsProgramsUnavailableTherapists || []
                        : appointmentsProgramsTherapist || []
                    }
                    value={
                      (showUnavailableTherapists
                        ? appointmentsProgramsUnavailableTherapists
                        : appointmentsProgramsTherapist
                      )?.find((o) => o.value === form?.therapistsId) || undefined
                    }
                    placeholder={t("SELECT_PLACEHOLDER")}
                    className={styles.select}
                    onChange={(value) => {
                      value &&
                        setForm((prev) => ({
                          ...prev,
                          therapistsGuid: value.therapistsGuid,
                          available: checkAvailability(value.therapistsId),
                        }));
                    }}
                    isDisabled={!isValidForChanges}
                  />
                )
              ) : (
                <p className={`${styles.form__note}`}>
                  {t("FILL_REQUIRED_FIELDS")}
                </p>
              )}
            </li>
            <li>
              <label htmlFor="comments" className={styles.form__label}>
                {t("COMMENTS")}
              </label>
              <textarea
                ref={textAreaRef}
                rows={1}
                className={`${styles.form__input} ${!isValidForChanges && styles.form__input_disabled
                  }`}
                name="comments"
                id="comments"
                value={form.comments || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, comments: e.target.value }))
                }
                disabled={!isValidForChanges}
              />
            </li>
          </ul>
          {createDate && (
            <p className={styles.form__note}>
              {t('CREATION_DATE_1')}{getPrintDate(new Date(createDate))}
            </p>
          )}
          <button
            type={"button"}
            className={`button ${styles.submitBtn} ${(!isValidForChanges || !isFormValid() || isSending) &&
              `button_disabled`
              }`}
            onClick={sendForm}
            disabled={isSending || !isValidForChanges || !isFormValid()}
          >
            {isSending ? t("SUBMITTING_LABEL") : t("KEEP_CHANGES")}
          </button>
        </form>
        {showCompaniesProgramsModal && (
        <Modal onClose={() => setShowCompaniesProgramsModal(false)} prevencherAnswer={true}>
          <CompanyProgramsPopup
            companyId={therapist.companyId}
            onClick={onCompanyProgramClick}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
              onClose?.();
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => {
          setOpenModalError(false);
          setErrorMessage(null);
        }}>
          <ErrorPopup 
            text={errorMessage || undefined}
            onClose={() => {
              setOpenModalError(false);
              setErrorMessage(null);
            }} 
          />
        </Modal>
      )}
    </section>
  );
}

export default AddTreatmentPlan;
